import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { captureMessage } from '@sentry/nextjs';
import { appVersion, getBaseUrl } from './constants';
import { timeConversion } from '../../utility/k_time';
import { IConfig } from '../config';
import { getDefaultJwt } from '../../service/session/dsi';
import AuthManager from '../auth';
import { getAuth, setAuth } from '../../service/session/auth';
import apiEndpoints from '../auth/const';
import { loyaltyApiUrlPrefix, menuApiUrlPrefix } from '..';

class APIInstance {
    private countryConfig: { [key: string]: IConfig } = {};

    public readonly axios: AxiosInstance;

    private authHealthCheckStatus: 'success' | 'in-progress' | 'fail';

    constructor() {
        const axiosInstance = axios.create({
            baseURL: getBaseUrl().http,
            headers: {
                Accepted: 'application/json',
                'Content-Type': 'application/json',
                'Qlub-App-Version': appVersion,
            },
            timeout: 15000,
        });

        axiosRetry(axiosInstance, {
            retryCondition: (error) => {
                return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNABORTED';
            },
        });

        axiosInstance.interceptors.request.use(this.requestInterceptor.bind(this));
        axiosInstance.interceptors.response.use(this.responseInterceptor.bind(this), this.errorInterceptor.bind(this));

        this.axios = axiosInstance;

        if (getAuth().JWTToken) {
            this.authHealthCheckStatus = 'success';
        } else {
            this.authHealthCheckStatus = 'in-progress';
            setTimeout(() => {
                this.authHealthCheckStatus = getAuth().JWTToken ? 'success' : 'fail';
            }, 20000);
        }
    }

    private checkCondition(): boolean {
        if (!this.countryConfig || !process.browser) {
            return false;
        }

        const { pathname } = location;

        if (pathname.indexOf('/qr/') !== 0) {
            return false;
        }

        const [, cc, slug] = pathname.split('/');

        if (!cc || !slug) {
            return false;
        }

        const config = this.countryConfig[cc];

        if (config?.apiCondition === 'new') {
            return true;
        }

        if (
            config?.apiCondition === 'new_by_vendor' &&
            config?.apiConditionVendors?.map((o) => o?.split('|')?.[1]).includes(slug)
        ) {
            return true;
        }

        return false;
    }

    private async requestInterceptor(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
        if (config.headers && this.checkCondition()) {
            config.headers['Qlub-Route-Tag'] = 'RC';
        }
        console.debug(`%cRequest:${config.method}:${config.url}`, 'color: #f9d71c', config.data);

        // @ts-ignore
        config.metadata = config.metadata || { et: Date.now() };

        this.authHealthCheck();

        // menu server
        if (config.url?.startsWith(menuApiUrlPrefix)) {
            const token = await AuthManager.getInstance().menuAuth();
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }

        // core edge
        const token = getDefaultJwt();
        if (token && config.headers) {
            config.headers['Qlub-Bill-Protect'] = `Bearer ${token}`;
        }
        const authToken = getAuth();
        if (authToken.JWTToken && config.headers) {
            config.headers['Qlub-User-Auth'] = `Bearer ${authToken.JWTToken}`;
        }
        return config;
    }

    private async responseInterceptor(response: AxiosResponse): Promise<AxiosResponse> {
        // @ts-ignore
        const diff = response?.config?.metadata?.et ? Date.now() - (response?.config?.metadata?.et || 0) : null;
        console.debug(
            `%cResponse:${response?.config?.method}:${response?.config?.url}`,
            'color: #f967a0',
            { res: response.data },
            diff ? timeConversion(diff) : '',
        );
        return response;
    }

    private async errorInterceptor(error: any): Promise<Promise<never>> {
        const { response, config: request } = error;

        // menu server
        if (request?.url?.startsWith(menuApiUrlPrefix) && response?.status === 401) {
            try {
                const token = await AuthManager.getInstance().menuRefresh();
                if (token && request.headers) {
                    request.headers.Authorization = `Bearer ${token}`;
                }
            } catch (e) {
                const token = await AuthManager.getInstance().menuAuth(true);
                if (token && request.headers) {
                    request.headers.Authorization = `Bearer ${token}`;
                }
            }
            return this.axios.request(request);
        }

        // core edge
        if (request?.url !== apiEndpoints.auth.refreshToken && [401].includes(response?.status)) {
            try {
                const token = await AuthManager.getInstance().refresh();
                if (token && request.headers) {
                    request.headers['Qlub-User-Auth'] = `Bearer ${token}`;
                }
            } catch (e) {
                if (request.retried) {
                    return Promise.reject(error);
                }
                delete request.headers['Qlub-User-Auth'];
                request.retried = true;
                return this.axios.request(request);
            }
            if (request?.url?.startsWith(loyaltyApiUrlPrefix)) {
                return axios.request(request);
            }
            return this.axios.request(request);
        }

        return Promise.reject(error);
    }

    public Get<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.axios.get(url, config);
    }

    public Post<T, B = Record<string, unknown>, R = AxiosResponse<T>>(
        url: string,
        data?: B,
        config?: AxiosRequestConfig,
    ): Promise<R> {
        return this.axios.post(url, data, config);
    }

    public Put<T, B = Record<string, unknown>, R = AxiosResponse<T>>(
        url: string,
        data?: B,
        config?: AxiosRequestConfig,
    ): Promise<R> {
        return this.axios.put(url, data, config);
    }

    public Patch<T, B = Record<string, unknown>, R = AxiosResponse<T>>(
        url: string,
        data?: B,
        config?: AxiosRequestConfig,
    ): Promise<R> {
        return this.axios.patch(url, data, config);
    }

    public Delete<T, B = Record<string, unknown>, R = AxiosResponse<T>>(
        url: string,
        data: B,
        config?: AxiosRequestConfig,
    ): Promise<R> {
        return this.axios.delete(url, {
            ...config,
            data: {
                ...data,
            },
        });
    }

    public setConfig(cc: string, config: IConfig): void {
        this.countryConfig[cc] = config;
    }

    private authHealthCheck() {
        if (['success', 'in-progress'].includes(this.authHealthCheckStatus)) {
            return;
        }

        if (getAuth().JWTToken) {
            return;
        }

        this.authHealthCheckStatus = 'in-progress';

        AuthManager.getInstance()
            .createToken({
                reCaptcha: '__ignore__',
            })
            .then((res) => {
                setAuth(res);
                this.authHealthCheckStatus = 'success';
                captureMessage('AuthHealthCheck done!');
            })
            .catch((err) => {
                this.authHealthCheckStatus = 'fail';
                captureMessage('AuthHealthCheck failed!', {
                    level: 'warning',
                    extra: {
                        message: err?.message,
                        err,
                    },
                });
            });
    }
}

const axiosInstance = new APIInstance();
export default axiosInstance;
